/**
 * Typographie française premium — formatters partagés.
 *
 * Règles strictes (validées par Yovan) appliquées partout : tableaux,
 * graphiques, tooltips, lettres et analyses textuelles. Le pendant backend
 * est `backend/services/fr_formatters.py` et doit rester strictement aligné.
 *
 *   - Séparateur de milliers : espace insécable (U+00A0)
 *   - Séparateur décimal     : virgule
 *   - Espace insécable avant le symbole € : "38,40 €"
 *   - Pas d'espace avant %               : "28,7%"
 *   - Espace normal avant un mot (jours, mois, pts) : "24 jours"
 *   - Valeurs négatives : signe moins typographique U+2212 ("−"), pas un tiret
 *   - Valeur manquante  : tiret cadratin "—" (U+2014)
 */

export const NBSP = ' ';       // espace insécable
export const MINUS = '−';      // signe moins typographique
export const EM_DASH = '—';         // U+2014, valeur manquante

// Intl.NumberFormat émet U+202F (NARROW NO-BREAK SPACE) selon la locale —
// on normalise systématiquement vers U+00A0 pour matcher le backend.
const NARROW_NBSP = ' ';

const isMissing = (v) => v === null || v === undefined || (typeof v === 'number' && isNaN(v));

const toNumber = (v) => {
  if (isMissing(v)) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Formate un nombre en français.
 *   formatNumberFr(142380)        -> "142 380"
 *   formatNumberFr(28.7, 1)       -> "28,7"
 *   formatNumberFr(-2.34, 1)      -> "−2,3"
 *   formatNumberFr(null)          -> "—"
 */
export function formatNumberFr(value, decimals = 0) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  const negative = n < 0;
  const abs = Math.abs(n);
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(abs).replace(new RegExp(NARROW_NBSP, 'g'), NBSP);
  return negative ? MINUS + formatted : formatted;
}

/**
 * Formate un montant en euros avec espace insécable avant €.
 *   formatCurrencyFr(38.40, 2) -> "38,40 €"
 *   formatCurrencyFr(142380)   -> "142 380 €"
 */
export function formatCurrencyFr(value, decimals = 0) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  return formatNumberFr(n, decimals) + NBSP + '€';
}

/**
 * Formate un pourcentage SANS espace avant %.
 *   formatPercentageFr(28.7)       -> "28,7%"
 *   formatPercentageFr(-2.34, 2)   -> "−2,34%"
 */
export function formatPercentageFr(value, decimals = 1) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  return formatNumberFr(n, decimals) + '%';
}

/**
 * Formate une durée en jours, pluriel automatique.
 *   formatDaysFr(24) -> "24 jours"
 *   formatDaysFr(1)  -> "1 jour"
 */
export function formatDaysFr(value, decimals = 0) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  const label = Math.abs(n) < 2 ? 'jour' : 'jours';
  return formatNumberFr(n, decimals) + ' ' + label;
}

/**
 * Formate un écart en points (signe toujours visible, utile pour les
 * comparaisons vs benchmark).
 *   formatPointsFr(2.3)  -> "+2,3 pts"
 *   formatPointsFr(-2.3) -> "−2,3 pts"
 *   formatPointsFr(0)    -> "0 pts"
 */
export function formatPointsFr(value, decimals = 1) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  const sign = n > 0 ? '+' : '';
  return sign + formatNumberFr(n, decimals) + ' pts';
}
